import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatAUD } from "@/lib/media";
import { useSeo } from "@/lib/seo";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Paw } from "@/components/decor";

export default function CartPage() {
  useSeo("Your Bag", "Review your Chewie & Solo goodies before checkout.");
  const { cart, removeFromCart, updateQty, subtotal } = useStore();
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    api.get("/products?best_seller=true&limit=4").then(({ data }) => setSuggestions(data.products)).catch(() => {});
  }, []);

  if (cart.length === 0) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <Paw className="w-20 h-20 text-[#FFD93D] mx-auto" />
      <h1 className="font-display font-extrabold text-3xl text-[#19324A] mt-6">Your shopping bag is looking a little lonely. 🐾</h1>
      <Link to="/shop" className="btn-primary mt-6 inline-flex" data-testid="empty-cart-page-btn">GO SNIFFING FOR GOODIES</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#19324A] mb-8">Your Bag</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cart.map((item) => (
            <div key={item.key} className="flex gap-4 bg-white rounded-3xl p-4 soft-shadow" data-testid={`cartpage-item-${item.slug}`}>
              <Link to={item.type === "bundle" ? "/bundle-builder" : `/product/${item.slug}`}><img src={item.image} alt={item.name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover bg-[#FFF9EE]" /></Link>
              <div className="flex-1 min-w-0">
                {item.type === "bundle" ? (
                  <p className="font-display font-semibold text-[#19324A]">{item.name}</p>
                ) : (
                  <Link to={`/product/${item.slug}`} className="font-display font-semibold text-[#19324A] hover:text-[#FF6B9D]">{item.name}</Link>
                )}
                {item.type === "bundle" ? (
                  <ul className="text-sm text-[#19324A]/55 mt-1 space-y-0.5">
                    {item.components.map((c) => <li key={`${c.product_id || c.slug || c.name}-${c.colour || ""}-${c.size || ""}`}>• {c.name}{(c.colour || c.size) ? ` (${[c.colour, c.size].filter(Boolean).join(", ")})` : ""}</li>)}
                  </ul>
                ) : (item.size || item.colour || item.personalisation) && <p className="text-sm text-[#19324A]/55 mt-1">{[item.colour, item.size].filter(Boolean).join(" · ")}{item.personalisation && <span className="text-[#FF6B9D] font-semibold">{(item.colour || item.size) ? " · " : ""}“{item.personalisation}”</span>}</p>}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 bg-[#FFF9EE] rounded-full px-1">
                    <button onClick={() => updateQty(item.key, item.quantity - 1)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white"><Minus className="w-4 h-4" /></button>
                    <span className="w-6 text-center font-semibold">{item.quantity}</span>
                    <button onClick={() => updateQty(item.key, item.quantity + 1)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white"><Plus className="w-4 h-4" /></button>
                  </div>
                  <span className="font-display font-bold text-lg text-[#19324A]">{formatAUD(item.price * item.quantity)}</span>
                </div>
              </div>
              <button onClick={() => removeFromCart(item.key)} className="text-[#19324A]/40 hover:text-[#FF6B9D] self-start" aria-label="Remove"><Trash2 className="w-5 h-5" /></button>
            </div>
          ))}
        </div>

        <div className="lg:sticky lg:top-24 self-start bg-white rounded-3xl p-6 soft-shadow h-fit">
          <h2 className="font-display font-bold text-xl text-[#19324A] mb-4">Order Summary</h2>
          <div className="flex justify-between text-[#19324A]/70 mb-2"><span>Subtotal</span><span className="font-semibold" data-testid="cartpage-subtotal">{formatAUD(subtotal)}</span></div>
          <p className="text-xs text-[#19324A]/50 mb-4">🐾 Shipping & taxes calculated at checkout.</p>
          <Link to="/checkout" className="btn-pink w-full" data-testid="cartpage-checkout-btn">Checkout <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/shop" className="btn-outline w-full mt-3 !py-2.5">Keep Shopping</Link>
        </div>
      </div>

      {suggestions.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display font-extrabold text-2xl text-[#19324A] mb-6">You might also like…</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">{suggestions.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        </section>
      )}
    </div>
  );
}
